// frontend/src/components/dialogs/ReferenceRouteDialog.js
import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Map, AlertCircle, Plus, X, RotateCcw, Loader2, Trash2, Languages, ChevronDown, ChevronRight, GitBranch, Check, Split } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import TranslationModal from '../common/TranslationModal';
import referenceService from '../../services/referenceService';
import SegmentVariantSelector from './SegmentVariantSelector';
import SegmentShortcutSelector from './SegmentShortcutSelector';
import ConfirmDialogShell from './ConfirmDialogShell';

export const ReferenceRouteDialog = ({
    isOpen,
    onClose,
    routeId,
    entityId,
    tripId = null, // NEW: Required for segment variant creation
    existingTypes = [], // For backward compatibility, but will fetch from API
    onConfirm,
    loading = false
}) => {
    const { t } = useTranslation();
    const { background, text, border, components, semantic, cn } = useTheme();

    // Multi-step dialog flow state
    // Step 1: Select route type and action (replace or add variants/shortcuts)
    // Step 2: Segment variant selection (when "Add Variant Segments" selected)
    // Step 3: Shortcut segment selection (when "Add Shortcut Segments" selected)
    const [dialogStep, setDialogStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null); // For step 2/3 - which type to add variants/shortcuts to
    const [expandedTypes, setExpandedTypes] = useState({}); // Track expanded route types
    const [shortcutSegments, setShortcutSegments] = useState([]); // Shortcuts from route_segments

    // State management
    const [selectedAction, setSelectedAction] = useState('');
    const [routeTypes, setRouteTypes] = useState([]);
    const [customTypeCount, setCustomTypeCount] = useState(0);
    const [maxCustomTypes] = useState(4);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [isCreatingCustomType, setIsCreatingCustomType] = useState(false);
    const [customTypeName, setCustomTypeName] = useState('');
    const [customTypeIcon, setCustomTypeIcon] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pendingReferenceAction, setPendingReferenceAction] = useState(null);
    const [isConfirmingReferenceAction, setIsConfirmingReferenceAction] = useState(false);

    // Translation modal state
    const [translationModalOpen, setTranslationModalOpen] = useState(false);
    const [currentTranslationType, setCurrentTranslationType] = useState(null);
    const [translationStats, setTranslationStats] = useState({});
    const [entityDefaultLanguage, setEntityDefaultLanguage] = useState('en');

    // Fetch route types and shortcut segments from API
    useEffect(() => {
        if (isOpen && routeId && entityId) {
            fetchRouteTypes();
            fetchShortcutSegments();
        }
    }, [isOpen, routeId, entityId]);

    // Fetch shortcut segments defined in route_segments
    const fetchShortcutSegments = async () => {
        try {
            const response = await axios.get(`/api/trips/${entityId}/routes/${routeId}/shortcut-segments`);
            if (response.data.success) {
                setShortcutSegments(response.data.shortcuts || []);
                console.log('[ReferenceRouteDialog] Shortcut segments loaded:', response.data.shortcutCount);
            }
        } catch (error) {
            console.error('[ReferenceRouteDialog] Error fetching shortcut segments:', error);
            setShortcutSegments([]);
        }
    };

    const fetchRouteTypes = async () => {
        try {
            setIsLoadingTypes(true);
            console.log('[ReferenceRouteDialog] Fetching route types for:', { entityId, routeId });
            const response = await axios.get(`/api/trips/${entityId}/routes/${routeId}/types`);

            console.log('[ReferenceRouteDialog] API Response:', response.data);

            if (response.data.success) {
                setRouteTypes(response.data.routeTypes || []);
                setCustomTypeCount(response.data.customTypeCount || 0);
                console.log('[ReferenceRouteDialog] Route types set:', {
                    routeTypes: response.data.routeTypes,
                    customTypeCount: response.data.customTypeCount,
                    hasRepresentative: response.data.routeTypes?.some(t => t.type_key === 'representative')
                });

                // Load translation stats for route types
                await loadAllTranslationStats(response.data.routeTypes || []);
            }
        } catch (error) {
            console.error('[ReferenceRouteDialog] Error fetching route types:', error);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    const loadAllTranslationStats = async (types) => {
        try {
            // Get available languages for counting
            const languages = await referenceService.getLanguages();
            const nonDefaultLanguages = languages.filter(l => l.language_code !== entityDefaultLanguage);

            const statsMap = {};

            // Load translation stats for each route type
            for (const type of types) {
                if (!type.id) continue; // Skip if no ID

                try {
                    const response = await axios.get(`/api/translations/entity/${entityId}/translations`, {
                        params: {
                            contentType: 'route_type',
                            contentId: type.id,
                            isGlobal: 'false'
                        }
                    });

                    const data = response.data.data;
                    const fieldTranslations = data.translations['display_name'] || {};

                    // Count translated languages
                    const translatedCount = Object.keys(fieldTranslations).filter(
                        lang => lang !== entityDefaultLanguage && fieldTranslations[lang]
                    ).length;

                    statsMap[type.id] = {
                        translated: translatedCount,
                        total: nonDefaultLanguages.length
                    };
                } catch (error) {
                    console.error(`Error loading translation stats for type ${type.id}:`, error);
                    statsMap[type.id] = { translated: 0, total: nonDefaultLanguages.length };
                }
            }

            setTranslationStats(statsMap);
        } catch (error) {
            console.error('Error loading translation stats:', error);
        }
    };

    const handleActionSelect = (action) => {
        setSelectedAction(action);
        setIsCreatingCustomType(false);
    };

    const handleCreateCustomTypeClick = () => {
        setSelectedAction('create_custom');
        setIsCreatingCustomType(true);
    };

    // Toggle expand/collapse for a route type section
    const handleToggleExpand = (typeKey) => {
        setExpandedTypes(prev => ({
            ...prev,
            [typeKey]: !prev[typeKey]
        }));
    };

    // Handle "Add Variant Segments" selection
    const handleAddVariantSegments = (typeKey) => {
        setSelectedType(typeKey);
        setDialogStep(2);
    };

    // Handle "Add Shortcut Segments" selection
    const handleAddShortcutSegments = (typeKey) => {
        setSelectedType(typeKey);
        setDialogStep(3);
    };

    // Handle back from step 2 or 3 to step 1
    const handleBackToStep1 = () => {
        setDialogStep(1);
        setSelectedType(null);
    };

    // Handle successful variant save
    const handleVariantsSaved = (result) => {
        // Close dialog and notify parent
        onClose(null);
        resetState();
    };

    // Handle successful shortcut save
    const handleShortcutsSaved = (result) => {
        // Close dialog and notify parent
        onClose(null);
        resetState();
    };

    const normalizeCustomTypeKey = (name) => (
        name.toLowerCase().replace(/[^a-z0-9_-]/g, '_')
    );

    const translateOrFallback = (key, fallback, options) => {
        const translated = t(key, options);
        return translated && translated !== key ? translated : fallback;
    };

    const getRouteTypeDisplayName = (typeKey, fallbackName = '') => {
        if (fallbackName) {
            return fallbackName;
        }

        if (typeKey === 'representative') {
            return translateOrFallback('representativeRoute', 'Representative Route');
        }

        const matchingType = routeTypes.find(type => type.type_key === typeKey);
        return matchingType?.display_name || typeKey;
    };

    const openReferenceActionConfirmation = () => {
        if (!selectedAction) return;

        if (isCreatingCustomType && selectedAction === 'create_custom') {
            const typeName = customTypeName.trim();
            if (!typeName) return;

            setPendingReferenceAction({
                typeKey: normalizeCustomTypeKey(typeName),
                displayName: typeName,
                icon: customTypeIcon,
                createsCustomType: true,
                exists: false
            });
            return;
        }

        setPendingReferenceAction({
            typeKey: selectedAction,
            displayName: getRouteTypeDisplayName(selectedAction),
            createsCustomType: false,
            exists: existingTypes.includes(selectedAction)
        });
    };

    const handleConfirm = async () => {
        if (!pendingReferenceAction) return;

        if (pendingReferenceAction.createsCustomType) {
            try {
                setIsConfirmingReferenceAction(true);

                const response = await axios.post(`/api/trips/${entityId}/routes/${routeId}/types`, {
                    typeKey: pendingReferenceAction.typeKey,
                    displayName: pendingReferenceAction.displayName,
                    icon: pendingReferenceAction.icon
                });

                if (response.data.success) {
                    // After creating the type, confirm creation of reference route
                    if (onConfirm) {
                        await onConfirm(response.data.typeKey || pendingReferenceAction.typeKey);
                    }

                    // Reset form
                    setCustomTypeName('');
                    setCustomTypeIcon('');
                    setSelectedAction('');
                    setIsCreatingCustomType(false);
                    setPendingReferenceAction(null);
                    // Show success toast
                    toast.success(t('customRouteTypeCreated', { typeName: pendingReferenceAction.displayName }));
                    // Refresh route types
                    await fetchRouteTypes();
                }
            } catch (error) {
                console.error('Error creating custom route type:', error);
                toast.error(error.response?.data?.message || t('failedToCreateRouteType'));
                return;
            } finally {
                setIsConfirmingReferenceAction(false);
            }
        } else {
            // Regular route type selection
            try {
                setIsConfirmingReferenceAction(true);
                if (onConfirm) {
                    await onConfirm(pendingReferenceAction.typeKey);
                } else {
                    onClose(pendingReferenceAction.typeKey);
                }
                setPendingReferenceAction(null);
                setSelectedAction('');
            } finally {
                setIsConfirmingReferenceAction(false);
            }
        }
    };

    const handleDeleteType = async (typeKey, typeName) => {
        setTypeToDelete({ typeKey, typeName });
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!typeToDelete) return;

        try {
            setIsDeleting(true);
            const response = await axios.delete(
                `/api/trips/${entityId}/routes/${routeId}/types/${typeToDelete.typeKey}`
            );

            if (response.data.success) {
                // Show success toast
                toast.success(t('customRouteTypeDeleted', { typeName: typeToDelete.typeName }));

                // Refresh route types
                await fetchRouteTypes();

                // Close confirmation and reset
                setShowDeleteConfirm(false);
                setTypeToDelete(null);

                // If the deleted type was selected, clear selection
                if (selectedAction === typeToDelete.typeKey) {
                    setSelectedAction('');
                }
            }
        } catch (error) {
            console.error('Error deleting route type:', error);
            toast.error(error.response?.data?.message || t('failedToDeleteRouteType'));
        } finally {
            setIsDeleting(false);
        }
    };

    // Reset all dialog state
    const resetState = () => {
        setDialogStep(1);
        setSelectedType(null);
        setExpandedTypes({});
        setSelectedAction('');
        setIsCreatingCustomType(false);
        setCustomTypeName('');
        setCustomTypeIcon('');
        setPendingReferenceAction(null);
        setIsConfirmingReferenceAction(false);
    };

    const handleCancel = () => {
        if (!loading && !isConfirmingReferenceAction) {
            onClose(null);
            resetState();
        }
    };

    const handleOpenTranslationModal = (type, e) => {
        e.preventDefault();
        e.stopPropagation();
        setCurrentTranslationType(type);
        setTranslationModalOpen(true);
    };

    const handleCloseTranslationModal = () => {
        setTranslationModalOpen(false);
        setCurrentTranslationType(null);
    };

    const handleTranslationSave = async () => {
        // Refresh translation stats after saving
        await loadAllTranslationStats(routeTypes);
        handleCloseTranslationModal();
    };

    const canAddCustomType = () => {
        const hasRepresentative = routeTypes.some(t => t.type_key === 'representative');
        return hasRepresentative && customTypeCount < maxCustomTypes;
    };

    const getRepresentativeType = () => {
        return routeTypes.find(t => t.type_key === 'representative');
    };

    const getCustomTypes = () => {
        return routeTypes.filter(t => t.type_key !== 'representative');
    };

    const hasRepresentative = getRepresentativeType() !== undefined;
    const representativeRouteExists = existingTypes.includes('representative');
    const isReferenceActionPending = loading || isConfirmingReferenceAction;

    const getConfirmationMessage = () => {
        if (!pendingReferenceAction) {
            return '';
        }

        const { typeKey, displayName, exists } = pendingReferenceAction;
        const fallback = exists
            ? `The existing ${displayName} reference route will be permanently replaced with data from this completed trip.`
            : `A new ${displayName} reference route will be created from this completed trip.`;

        const systemRouteConfirmationKeys = {
            representative: exists ? 'replaceRepresentativeRouteConfirmation' : 'createRepresentativeRouteConfirmation',
            alternative: exists ? 'replaceAlternativeRouteConfirmation' : 'createAlternativeRouteConfirmation',
            seasonal: exists ? 'replaceSeasonalRouteConfirmation' : 'createSeasonalRouteConfirmation'
        };

        const confirmationKey = systemRouteConfirmationKeys[typeKey];
        if (confirmationKey) {
            return translateOrFallback(confirmationKey, fallback);
        }

        return translateOrFallback(
            exists ? 'replaceCustomReferenceRouteConfirmation' : 'createCustomReferenceRouteConfirmation',
            fallback,
            { typeName: displayName }
        );
    };

    const getConfirmationTitle = () => {
        if (!pendingReferenceAction) {
            return '';
        }

        return pendingReferenceAction.exists
            ? translateOrFallback('replaceReferenceRoute', 'Replace Reference Route')
            : translateOrFallback('createReferenceRoute', 'Create Reference Route');
    };

    // Determine dialog title based on step
    const getDialogTitle = () => {
        if (dialogStep === 2) {
            return t('addVariantSegments');
        }
        if (dialogStep === 3) {
            return t('addShortcutSegments');
        }
        return t('createOrReplaceReferenceRoute');
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleCancel}
                title={getDialogTitle()}
                size={dialogStep === 2 || dialogStep === 3 ? 'lg' : 'md'}
                closeOnBackdropClick={!loading}
                footer={dialogStep === 1 ? (
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={openReferenceActionConfirmation}
                            disabled={loading || !selectedAction || (isCreatingCustomType && !customTypeName.trim())}
                            className={cn(
                                "px-4 py-2 rounded flex items-center gap-2",
                                loading || !selectedAction || (isCreatingCustomType && !customTypeName.trim())
                                    ? components.button.disabled
                                    : components.button.primary
                            )}
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Map className="w-5 h-5" />
                            )}
                            {loading ? (t('processing') || 'Processing...') : t('confirm')}
                        </button>
                        <button
                            onClick={handleCancel}
                            disabled={loading}
                            className={cn(
                                "px-4 py-2 rounded flex items-center gap-2",
                                loading
                                    ? components.button.disabled
                                    : components.button.secondary
                            )}
                        >
                            <X className="w-5 h-5" />
                            {t('cancel')}
                        </button>
                    </div>
                ) : null /* Step 2 has its own footer in SegmentVariantSelector */}
            >
                {/* Step 2: Segment Variant Selector */}
                {dialogStep === 2 && tripId && (
                    <SegmentVariantSelector
                        tripId={tripId}
                        entityId={entityId}
                        referenceType={selectedType}
                        onSave={handleVariantsSaved}
                        onCancel={handleBackToStep1}
                        loading={loading}
                    />
                )}

                {/* Step 3: Shortcut Segment Selector */}
                {dialogStep === 3 && tripId && (
                    <SegmentShortcutSelector
                        tripId={tripId}
                        routeId={routeId}
                        entityId={entityId}
                        referenceType={selectedType}
                        shortcutSegments={shortcutSegments}
                        onSave={handleShortcutsSaved}
                        onCancel={handleBackToStep1}
                        loading={loading}
                    />
                )}

                {/* Step 1: Route Type Selection */}
                {dialogStep === 1 && (
                <div className="space-y-4">
                    {/* Information section */}
                    <div className={cn("border rounded-lg p-3", semantic.info.background, border.input, semantic.info.text)}>
                        <div className="flex items-start gap-2">
                            <AlertCircle className={cn("w-5 h-5 flex-shrink-0 mt-0.5", semantic.info.accent)} />
                            <div className="flex-1">
                                <p className="text-sm">
                                    {t('selectReferenceRouteType')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {isLoadingTypes ? (
                        <div className="flex justify-center items-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Representative Route (expandable section) */}
                            <div className={cn("border rounded-lg", border.input, background.card)}>
                                {/* Header - click to expand */}
                                <button
                                    type="button"
                                    onClick={() => handleToggleExpand('representative')}
                                    className={cn(
                                        "w-full flex items-center justify-between p-3 text-left",
                                        "hover:" + background.hover,
                                        "transition-colors rounded-lg"
                                    )}
                                    disabled={loading}
                                >
                                    <div className={cn("font-medium flex items-center gap-2", text.primary)}>
                                        {representativeRouteExists ? (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30">
                                                <Check className="w-3 h-3 text-green-600" />
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800">
                                                <Plus className="w-3 h-3 text-gray-500" />
                                            </span>
                                        )}
                                        {t('representativeRoute')}
                                        {representativeRouteExists && (
                                            <span className={cn("text-xs px-2 py-0.5 rounded-full", "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400")}>
                                                {t('exists') || 'Exists'}
                                            </span>
                                        )}
                                    </div>
                                    {expandedTypes['representative'] ? (
                                        <ChevronDown className={cn("w-5 h-5", text.secondary)} />
                                    ) : (
                                        <ChevronRight className={cn("w-5 h-5", text.secondary)} />
                                    )}
                                </button>

                                {/* Expanded content with sub-options */}
                                {expandedTypes['representative'] && (
                                    <div className={cn("px-3 pb-3 space-y-2 border-t", border.default)}>
                                        {/* Replace option */}
                                        <label className={cn(
                                            "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors mt-2",
                                            selectedAction === 'representative' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : 'hover:' + background.hover
                                        )}>
                                            <input
                                                type="radio"
                                                name="referenceAction"
                                                value="representative"
                                                checked={selectedAction === 'representative'}
                                                onChange={() => handleActionSelect('representative')}
                                                disabled={loading}
                                            />
                                            <div className="flex items-center gap-2">
                                                <RotateCcw className="w-4 h-4 text-amber-500" />
                                                <span className={cn("font-medium", text.primary)}>
                                                    {representativeRouteExists ? t('replaceRepresentativeRoute') : t('createRepresentativeRoute')}
                                                </span>
                                            </div>
                                        </label>

                                        {/* Add Variant Segments option (only shown if representative exists and tripId provided) */}
                                        {hasRepresentative && tripId && (
                                            <button
                                                type="button"
                                                onClick={() => handleAddVariantSegments('representative')}
                                                className={cn(
                                                    "w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors text-left",
                                                    "hover:" + background.hover
                                                )}
                                                disabled={loading}
                                            >
                                                <div className="w-4" /> {/* Spacer for alignment */}
                                                <div className="flex items-center gap-2">
                                                    <GitBranch className="w-4 h-4 text-blue-500" />
                                                    <span className={cn("font-medium", text.primary)}>
                                                        {t('addVariantSegments')}
                                                    </span>
                                                </div>
                                                <span className={cn("text-xs ml-auto", text.secondary)}>
                                                    {t('addVariantSegmentsDescription')}
                                                </span>
                                            </button>
                                        )}

                                        {/* Add Shortcut Segments option (only shown if representative exists, tripId provided, and shortcuts are defined) */}
                                        {hasRepresentative && tripId && shortcutSegments.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => handleAddShortcutSegments('representative')}
                                                className={cn(
                                                    "w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors text-left",
                                                    "hover:" + background.hover
                                                )}
                                                disabled={loading}
                                            >
                                                <div className="w-4" /> {/* Spacer for alignment */}
                                                <div className="flex items-center gap-2">
                                                    <Split className="w-4 h-4 text-purple-500" />
                                                    <span className={cn("font-medium", text.primary)}>
                                                        {t('addShortcutSegments')}
                                                    </span>
                                                </div>
                                                <span className={cn("text-xs ml-auto", text.secondary)}>
                                                    {t('addShortcutSegmentsDescription', { count: shortcutSegments.length })}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Custom Types (expandable sections) */}
                            {getCustomTypes().map((type) => {
                                const exists = existingTypes.includes(type.type_key);
                                const isSelected = selectedAction === type.type_key;
                                const typeStats = translationStats[type.id] || { translated: 0, total: 0 };
                                const isExpanded = expandedTypes[type.type_key];

                                return (
                                    <div key={type.type_key} className={cn("border rounded-lg", border.input, background.card)}>
                                        {/* Header - click to expand */}
                                        <div className="flex items-center justify-between p-3">
                                            <button
                                                type="button"
                                                onClick={() => handleToggleExpand(type.type_key)}
                                                className={cn(
                                                    "flex-1 flex items-center gap-2 text-left",
                                                    "transition-colors"
                                                )}
                                                disabled={loading}
                                            >
                                                <div className={cn("font-medium flex items-center gap-2", text.primary)}>
                                                    {exists ? (
                                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30">
                                                            <Check className="w-3 h-3 text-green-600" />
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800">
                                                            <Plus className="w-3 h-3 text-gray-500" />
                                                        </span>
                                                    )}
                                                    {type.icon && <span>{type.icon}</span>}
                                                    {type.display_name}
                                                    {exists && (
                                                        <span className={cn("text-xs px-2 py-0.5 rounded-full", "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400")}>
                                                            {t('exists') || 'Exists'}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                            <div className="flex items-center gap-2">
                                                {/* Manage Translations Button */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleOpenTranslationModal(type, e)}
                                                    className={cn(
                                                        "inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors",
                                                        "hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400",
                                                        border.default, "border"
                                                    )}
                                                    disabled={loading}
                                                    title={t('manage_translations')}
                                                >
                                                    <Languages className="w-3.5 h-3.5" />
                                                    {typeStats.total > 0 && (
                                                        <span className={cn(
                                                            "px-1 py-0.5 text-xs rounded-full",
                                                            typeStats.translated > 0
                                                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                                        )}>
                                                            {typeStats.translated}/{typeStats.total}
                                                        </span>
                                                    )}
                                                </button>

                                                {/* Delete Button */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleDeleteType(type.type_key, type.display_name);
                                                    }}
                                                    className={cn(
                                                        "p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors",
                                                        "text-red-600 dark:text-red-400"
                                                    )}
                                                    disabled={loading}
                                                    title={t('deleteCustomRouteType')}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>

                                                {/* Expand/collapse toggle */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleExpand(type.type_key)}
                                                    className={cn("p-1", text.secondary)}
                                                    disabled={loading}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown className="w-5 h-5" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded content with sub-options */}
                                        {isExpanded && (
                                            <div className={cn("px-3 pb-3 space-y-2 border-t", border.default)}>
                                                {/* Replace option */}
                                                <label className={cn(
                                                    "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors mt-2",
                                                    isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : 'hover:' + background.hover
                                                )}>
                                                    <input
                                                        type="radio"
                                                        name="referenceAction"
                                                        value={type.type_key}
                                                        checked={isSelected}
                                                        onChange={() => handleActionSelect(type.type_key)}
                                                        disabled={loading}
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <RotateCcw className="w-4 h-4 text-amber-500" />
                                                        <span className={cn("font-medium", text.primary)}>
                                                            {exists ? t('replaceRoute') || 'Replace Route' : t('createRoute') || 'Create Route'}
                                                        </span>
                                                    </div>
                                                </label>

                                                {/* Add Variant Segments option (only shown if type exists and tripId provided) */}
                                                {exists && tripId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddVariantSegments(type.type_key)}
                                                        className={cn(
                                                            "w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors text-left",
                                                            "hover:" + background.hover
                                                        )}
                                                        disabled={loading}
                                                    >
                                                        <div className="w-4" /> {/* Spacer for alignment */}
                                                        <div className="flex items-center gap-2">
                                                            <GitBranch className="w-4 h-4 text-blue-500" />
                                                            <span className={cn("font-medium", text.primary)}>
                                                                {t('addVariantSegments')}
                                                            </span>
                                                        </div>
                                                        <span className={cn("text-xs ml-auto", text.secondary)}>
                                                            {t('addVariantSegmentsDescription')}
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                                {/* Create New Custom Type Option */}
                                {canAddCustomType() && (
                                    <div className={cn(
                                        "border rounded-lg p-3",
                                        border.input,
                                        background.card
                                    )}>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="referenceAction"
                                                value="create_custom"
                                                checked={isCreatingCustomType}
                                                onChange={handleCreateCustomTypeClick}
                                                className="mt-1"
                                                disabled={loading}
                                            />
                                            <div className="flex-1">
                                                <div className={cn("font-medium flex items-center gap-2", text.primary)}>
                                                    <Plus className="w-4 h-4 text-blue-500" />
                                                    {t('createCustomRouteType')}
                                                </div>
                                                <p className={cn("text-sm mt-1", text.secondary)}>
                                                    {t('customTypesUsed', { count: customTypeCount, max: maxCustomTypes })}
                                                </p>

                                                {/* Custom type input fields (shown when selected) */}
                                                {isCreatingCustomType && (
                                                    <div className="mt-3 space-y-3">
                                                        <div>
                                                            <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                                                                {t('customRouteTypeName')}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={customTypeName}
                                                                onChange={(e) => setCustomTypeName(e.target.value)}
                                                                placeholder={t('customRouteTypeNamePlaceholder')}
                                                                className={cn(
                                                                    "w-full px-3 py-2 rounded border",
                                                                    border.input,
                                                                    background.input,
                                                                    text.primary
                                                                )}
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                                                                {t('icon')} ({t('optional')})
                                                            </label>
                                                            <div className="relative">
                                                                <input
                                                                    type="text"
                                                                    value={customTypeIcon}
                                                                    onChange={(e) => setCustomTypeIcon(e.target.value)}
                                                                    placeholder="🚌"
                                                                    className={cn(
                                                                        "w-full px-3 py-2 pr-10 rounded border",
                                                                        border.input,
                                                                        background.input,
                                                                        text.primary
                                                                    )}
                                                                    disabled={loading}
                                                                    maxLength={2}
                                                                />
                                                                {customTypeIcon && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCustomTypeIcon('')}
                                                                        className={cn(
                                                                            "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors",
                                                                            text.secondary
                                                                        )}
                                                                        disabled={loading}
                                                                        title={t('clearIcon') || 'Clear icon'}
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <p className={cn("text-xs mt-1", text.secondary)}>
                                                                {t('iconHelperText') || 'Enter an emoji (e.g., 🚌 🚐 🚍) or leave empty for no icon'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                )}

                                {/* Show message if custom types are disabled */}
                                {!hasRepresentative && (
                                    <div className={cn("p-3 rounded-lg", semantic.warning.background, semantic.warning.text)}>
                                        <p className="text-sm">{t('representativeRequired')}</p>
                                    </div>
                                )}

                                {hasRepresentative && customTypeCount >= maxCustomTypes && (
                                    <div className={cn("p-3 rounded-lg", semantic.warning.background, semantic.warning.text)}>
                                        <p className="text-sm">{t('customTypeMaxReached')}</p>
                                    </div>
                                )}
                        </>
                    )}
                </div>
                )}
            </Modal>

            <ConfirmDialogShell
                isOpen={!!pendingReferenceAction}
                onClose={() => !isReferenceActionPending && setPendingReferenceAction(null)}
                onConfirm={handleConfirm}
                title={getConfirmationTitle()}
                size="sm"
                confirmLabel={
                    pendingReferenceAction?.exists
                        ? translateOrFallback('replace', 'Replace')
                        : translateOrFallback('create', 'Create')
                }
                confirmVariant={pendingReferenceAction?.exists ? 'warning' : 'primary'}
                confirmIcon={pendingReferenceAction?.exists ? RotateCcw : Map}
                isLoading={isReferenceActionPending}
                isDisabled={isReferenceActionPending}
                showIcon={true}
                iconVariant={pendingReferenceAction?.exists ? 'warning' : 'info'}
                closeOnBackdropClick={!isReferenceActionPending}
            >
                <div className="space-y-3">
                    <p className={cn("text-sm", text.secondary)}>
                        {getConfirmationMessage()}
                    </p>
                    {pendingReferenceAction?.displayName && (
                        <div className={cn("text-sm rounded-lg p-3", semantic.info.background, semantic.info.text)}>
                            <span className="font-medium">{pendingReferenceAction.displayName}</span>
                        </div>
                    )}
                </div>
            </ConfirmDialogShell>

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && typeToDelete && (
                <ConfirmDialogShell
                    isOpen={showDeleteConfirm}
                    onClose={() => !isDeleting && setShowDeleteConfirm(false)}
                    title={t('deleteCustomRouteType')}
                    size="sm"
                    onConfirm={confirmDelete}
                    confirmLabel={t('delete')}
                    confirmVariant="danger"
                    confirmIcon={Trash2}
                    isLoading={isDeleting}
                    isDisabled={isDeleting}
                    showIcon={true}
                    iconVariant="danger"
                    closeOnBackdropClick={!isDeleting}
                >
                    <div className="space-y-4">
                        <div className={cn("p-3 rounded-lg", semantic.warning.background, semantic.warning.text)}>
                            <p className="text-sm">
                                {t('deleteCustomRouteTypeConfirm', { typeName: typeToDelete.typeName })}
                            </p>
                        </div>
                        <p className={cn("text-sm", text.secondary)}>
                            {t('deleteRouteTypeWarning')}
                        </p>
                    </div>
                </ConfirmDialogShell>
            )}

            {/* Translation Modal */}
            {translationModalOpen && currentTranslationType && (
                <TranslationModal
                    isOpen={translationModalOpen}
                    onClose={handleCloseTranslationModal}
                    entityId={entityId}
                    entityDefaultLanguage={entityDefaultLanguage}
                    contentType="route_type"
                    contentId={currentTranslationType.id}
                    fieldName="display_name"
                    defaultValue={currentTranslationType.original_display_name || currentTranslationType.display_name}
                    onSave={handleTranslationSave}
                    multiline={false}
                    isGlobal={false}
                    readOnly={false}
                />
            )}
        </>
    );
};

export default ReferenceRouteDialog;
